/*
  drs-colour-strip — swaps the product strip when a colour swatch is picked.

  HOW IT GETS THE PRODUCTS
  ------------------------
  It keeps no product index of its own. Each swatch already links to the
  collection filtered by that colour — with every raw spelling in the query
  string, built from snippets/drs-facet-map.liquid. The strip fetches that exact
  URL and lifts the rendered <product-block> cards out of the response.

  So the strip can never disagree with the collection page: same URL, same merge,
  same products. Picking "Black" here returns the same 107 products as ticking
  Black in the sidebar, including the FIGS items tagged BLK that a
  single-spelling link would miss.

  WITHOUT JAVASCRIPT the swatches remain ordinary links to those filtered
  collection pages, and the first colour is already server-rendered — so the
  section degrades to something useful rather than blank.

  NOTE ON DOM HANDLING: fetched markup is moved across as parsed nodes
  (importNode) rather than assigned through innerHTML. Same-origin theme output
  is not attacker-controlled, but adopting nodes keeps untrusted-looking strings
  out of the DOM path entirely and avoids re-parsing the same HTML twice.
*/
class DrsColourStrip extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-strip-track]');
    this.limit = parseInt(this.dataset.limit, 10) || 8;
    this.cache = new Map();

    // Seed the cache with what the server already rendered, so coming back to
    // the first colour costs no request.
    const seed = this.querySelector('[data-strip-initial]');
    if (seed && this.track) {
      const frag = document.createDocumentFragment();
      [...this.track.children].forEach((li) => frag.appendChild(li.cloneNode(true)));
      // Server render is the first colour with no gender applied, so key it as ''.
      this.cache.set(`::${seed.value}`, frag);
    }

    this.base = this.dataset.base || '/collections/all';
    this.gender = '';

    this.addEventListener('click', (evt) => {
      const gender = evt.target.closest('[data-gender]');
      if (gender) {
        this.gender = gender.dataset.gender || '';
        this.querySelectorAll('[data-gender]').forEach((b) => {
          const on = b === gender;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        const active = this.querySelector('[data-colour-url].is-active')
          || this.querySelector('[data-colour-url]');
        if (active) this.select(active);
        return;
      }

      const swatch = evt.target.closest('[data-colour-url]');
      if (!swatch) return;
      evt.preventDefault();
      this.select(swatch);
    });
  }

  /**
   * Gender rides as a tag segment on the collection path, which Shopify ANDs
   * with the colour filter query: /collections/x/male?filter.v.option.color=...
   */
  urlFor(swatch) {
    const seg = this.gender ? `/${this.gender}` : '';
    return `${this.base}${seg}?${swatch.dataset.colourQuery || ''}`;
  }

  select(swatch) {
    this.querySelectorAll('[data-colour-url]').forEach((el) => {
      const on = el === swatch;
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    const url = this.urlFor(swatch);

    const label = this.querySelector('[data-colour-label]');
    if (label) label.textContent = swatch.dataset.colourName || '';

    const shopAll = this.querySelector('[data-shop-all]');
    if (shopAll) shopAll.href = url;

    // Gender is part of the identity of a result set, so it is part of the key —
    // otherwise switching to Male would show the cached All-genders strip.
    // No "force refetch" flag. There used to be one, and it did nothing: it
    // skipped the cache and then the very next block painted from the cache
    // anyway. It was never needed — gender is in the key, so switching gender
    // is already a different cache entry, and a hit is always the right result.
    const key = `${this.gender}::${swatch.dataset.colourKey}`;
    const cached = this.cache.get(key);
    if (cached) {
      this.paint(cached.cloneNode(true));
      return;
    }

    this.setAttribute('aria-busy', 'true');
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        let cards = [...doc.querySelectorAll('product-block')];

        // With a gender chosen, push unisex products to the end. They belong in
        // the results — they are tagged for both genders and really are sellable
        // to both — but several lead with a male model photo, so seeing one
        // first under "Female" reads as a broken filter. Sorting rather than
        // hiding keeps them reachable. Stable: everything else holds its order.
        if (this.gender) {
          const unisex = (c) => c.hasAttribute('data-drs-unisex');
          cards = [...cards.filter((c) => !unisex(c)), ...cards.filter(unisex)];
        }

        cards = cards.slice(0, this.limit);
        const frag = document.createDocumentFragment();
        cards.forEach((card) => {
          const li = document.createElement('li');
          li.className = 'drs-strip__item';
          li.appendChild(document.importNode(card, true));
          frag.appendChild(li);
        });
        this.cache.set(key, frag);
        this.paint(frag.cloneNode(true));
      })
      .catch(() => {
        // Leave what is on screen rather than blanking the section — the swatch
        // is still a working link to the filtered collection.
        this.removeAttribute('aria-busy');
      });
  }

  paint(fragment) {
    this.track.replaceChildren(fragment);
    this.track.scrollTo({ left: 0, behavior: 'auto' });
    this.removeAttribute('aria-busy');
  }
}

if (!customElements.get('drs-colour-strip')) {
  customElements.define('drs-colour-strip', DrsColourStrip);
}
